// Constants
import { IProductCard } from '@src/components/product-card/product-card.types'
import { API_SF_RUNTIME_BASE_URL, CONFIG_BUILD_ENV } from '@src/config/env'
import ProductOptionValue, { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import {
  GetSearchProductRequest,
  ProductServiceOptions,
  GetProductRequest,
} from '@src/quince-site-common/src/services/productService/productService.types'
import { getCommaSeparatedAdditionalAttributesValueString } from '@src/quince-site-common/src/utils/additionalAtrributeUtility'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { PRODUCT_FILTER_KEYS, PRODUCT_SORT_VALUES } from '../../constants/product'
import { FEATURES, MODULES } from '../../constants/template-info'
import Product, { IProduct } from '../../domains/Product/Product'
import { DeliveryDetailsByColorType } from '../../domains/ProductDeliveryDetails'
import { IProductPurchaseInfo } from '../../domains/ProductPurchaseInfo'
import QuinceApiError from '../../domains/quince-api-error'
import productHelper, { CUSTOM_FIELD_NAMES } from '../../helpers/productHelper'
import { storefrontRuntimeHttpClient, bffClient, sandServicesHttpClient } from '../../utils/HttpClient'
import { getSvDetailsByIdForProductVariants } from '../checkoutService/CheckoutProductService'

import { fetchAndEnrichProducts } from './fetchAndEnrichProducts'

interface GetOrderedProductsRequest {
  productIds: number[]
  filters?: Record<PRODUCT_FILTER_KEYS, string>
  sortValue?: PRODUCT_SORT_VALUES
  additionalAttributes?: Record<string, string>
  limit?: number
  useLite?: boolean
}

interface IProductFeedResponse {
  data: {
    product: Product | null
    color_order: string[]
    website_product: Product
  }[]
  next_url: string | null
  total_counts: number
}

class ProductService {
  private getProductCardId(product: Product, selectedColor: string) {
    return `${product.id}_${selectedColor}`
  }

  public async getOrderedProducts(request: GetOrderedProductsRequest) {
    try {
      const initialUrlParams = new URLSearchParams({
        ...request.filters,
        productIds: request.productIds.join(','),
        sortBy: String(request.sortValue),
        limit: String(request.limit),
        useLite: String(!!request.useLite),
        environment: CONFIG_BUILD_ENV.toUpperCase(),
      })
      const additionalAttribute = getCommaSeparatedAdditionalAttributesValueString(request.additionalAttributes)
      if (additionalAttribute) {
        initialUrlParams.set('additionalAttribute', additionalAttribute)
      }

      let nextUrl: URL | null = new URL(`${API_SF_RUNTIME_BASE_URL}/get-all-products?${initialUrlParams.toString()}`)
      const allUniqueProductCardIDs: string[] = []

      while (nextUrl) {
        const url: URL = new URL(nextUrl)
        const paginatedProductFeedData = await storefrontRuntimeHttpClient.get<IProductFeedResponse>(url.href)
        allUniqueProductCardIDs.push(
          ...paginatedProductFeedData.data.data
            .filter(productData => Boolean(productData.website_product))
            .map(({ website_product, color_order }) => this.getProductCardId(website_product, color_order[0] ?? '')),
        )
        nextUrl = paginatedProductFeedData.data.next_url ? new URL(paginatedProductFeedData.data.next_url) : null
      }
      return allUniqueProductCardIDs
    } catch (e) {
      const quinceError = new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching products in product service getOrderedProducts() runtime',
        apiName: 'ALL_PRODUCT_FETCH',
      })
      captureException(quinceError, {
        module: MODULES.PRODUCT_SERVICE,
        feature: FEATURES.FILTER_CHANGE,
        extraInfo: {
          capturedFrom: 'getOrderedProducts',
        },
      })
      return []
    }
  }

  public async getProductDynamicDataByProductIds(
    productIds: (number | string)[],
    useLite = false,
    zipCode?: string | undefined,
  ) {
    try {
      const { data: tagsAndDeliveryInfoWithProductId } = await sandServicesHttpClient.get<
        Pick<
          IProduct,
          'id' | 'tags' | 'delivery_info' | 'new_color_options' | 'productOptions' | 'isWhiteGloveDeliveryAvailable'
        >[]
      >('/products/enrich', {
        params: {
          internalProductIds: productIds.join(','),
          options: ProductServiceOptions.ENRICH_ONLY_TAGS,
          useLite,
          zipCode,
        },
      })

      const productIdVsDeliveryInfo: Record<number, DeliveryDetailsByColorType | undefined> = {}
      const productIdVsTags: Record<number, ProductTag[]> = {}
      const colorOrderVsProductId: Record<number, ProductOptionValue[] | undefined> = {}
      const newColorOrderVsProductId: Record<number, ProductOptionValue[] | undefined> = {}
      const isWhiteGloveProductVsProductId: Record<number, boolean> = {}

      tagsAndDeliveryInfoWithProductId.forEach(productIdTagDeliveryInfoTuple => {
        productIdVsTags[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.tags
        productIdVsDeliveryInfo[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.delivery_info
        newColorOrderVsProductId[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.new_color_options
        colorOrderVsProductId[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.productOptions.find(
          option => option.name === ProductOptionName.Color,
        )?.values
        isWhiteGloveProductVsProductId[productIdTagDeliveryInfoTuple.id] =
          !!productIdTagDeliveryInfoTuple.isWhiteGloveDeliveryAvailable
      })

      return {
        productIdVsTags,
        productIdVsDeliveryInfo,
        colorOrderVsProductId,
        newColorOrderVsProductId,
        isWhiteGloveProductVsProductId,
      }
    } catch (e) {
      const quinceError = new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching product tags getProductDynamicDataByProductIds() runtime',
        apiName: 'WEBSITE_PRODUCT_FETCH',
      })
      captureException(quinceError, {
        module: MODULES.PRODUCT_SERVICE,
        feature: FEATURES.GET_PRODUCT,
        extraInfo: {
          capturedFrom: 'getProductDynamicDataByProductIds',
        },
      })
      return {}
    }
  }

  public async getProductCards({
    productIds,
    spids,
    colors,
  }: {
    productIds?: string[]
    spids?: string[]
    colors?: string[]
  }) {
    if (!spids?.length && !productIds?.length) {
      return []
    }
    const requestParams = {
      spids: spids?.join(','),
      productIds: productIds?.join(','),
      // @i18n-ignore
      type: 'DEFAULT',
      colors: colors?.join(','),
    }
    try {
      const {
        data: { data },
      } = await bffClient.get<{ data: IProductCard[] }>('/v1/product/get-product-cards', {
        params: requestParams,
      })
      return data
    } catch (e) {
      throw new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching product cards getProductCards() runtime',
        apiName: 'BFF_PRODUCT_CARDS_FETCH',
        data: {
          requestParams,
        },
      })
    }
  }

  public async getProducts(fetchProductRequest?: GetProductRequest): Promise<Product[]> {
    return await fetchAndEnrichProducts(fetchProductRequest)
  }

  public async getSearchResults(fetchProductRequest?: GetSearchProductRequest): Promise<{ data: IProductCard[] }> {
    try {
      const { data } = await bffClient.get<{ data: IProductCard[] }>('v1/search/search-results', {
        params: fetchProductRequest,
      })
      return data
    } catch (e) {
      throw new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching website product getSearchResults() runtime',
        apiName: 'WEBSITE_SEARCH_PRODUCT_FETCH',
        data: {
          fetchProductRequest,
        },
      })
    }
  }

  public async getProductPurchaseInfo(product: Product): Promise<IProductPurchaseInfo> {
    const [svidShowMTOMapPromise, svidVsSvDetails] = await Promise.all([
      this.getShowMadeToOrderInfoForProductVariants(product),
      getSvDetailsByIdForProductVariants(product),
    ])

    return {
      svidShowMTOMap: svidShowMTOMapPromise,
      svidVsSvDetails,
    }
  }

  public async getProductVariantIdToInventoryCount(productId: number) {
    const { data: variantToInventoryMap } = await storefrontRuntimeHttpClient.get<Record<string, number>>(
      '/get-variant-inventory-info',
      {
        params: {
          productId,
        },
      },
    )

    return variantToInventoryMap
  }

  /**
   * This returns whether to Show MTO for a variant or not.
   * For scenarios, where MTO is present in SF location, then don't show MTO.
   */
  private async getShowMadeToOrderInfoForProductVariants(product: Product): Promise<Record<number, boolean>> {
    if (!productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_MTO)) {
      return {}
    }
    const { data: variantIdIsInventoryInSF } = await storefrontRuntimeHttpClient.get<Record<number, boolean>>(
      '/get-made-to-order-availability-in-sf',
      {
        params: {
          variantIds: (product.variants ?? []).map(v => v.svid).join(','),
        },
      },
    )
    const variantIdShowMTOMap: Record<number, boolean> = {}
    for (const { variantId, id } of (product.variants ?? []).map(v => ({
      variantId: v.svid,
      id: v.id,
    }))) {
      if (!variantIdIsInventoryInSF[variantId]) {
        variantIdShowMTOMap[id] = true
      }
    }
    return variantIdShowMTOMap
  }

  getProductsBySpids = async (spids: (number | string)[]) => {
    const products = await this.getProducts({
      spids: spids,
      useLite: true,
      options: [ProductServiceOptions.ENRICH_REFRESH_LITE],
    })
    const productBySpid: Record<string, Product> = {}

    // This is fix for 100+ variants
    products.forEach(product => {
      const spids = Array.from(new Set((product.variants ?? []).map(variant => variant.spid)))
      spids.forEach(spid => {
        productBySpid[spid] = product
      })
    })

    return productBySpid
  }
}

const productService = new ProductService()

export default productService
